import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateEffect } from 'react-use';
import { Grid, Button, TextField, Alert } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
// import EmailInfo from '../../components/manager/EmailInfo';
import {
  checkInfo,
  checkUserId,
  confirmCode,
  sendVerifyMsg,
  setValue,
  uncheck,
} from '../../modules/signup';
import VerifiedField from '../profile/VerifiedField';

const errorCheckList = [
  'userId',
  'password',
  'passwordConfirm',
  'lastName',
  'firstName',
];

const SignupContainer = () => {
  const dispatch = useDispatch();
  const signup = useSelector((state) => state.signup);
  const { checked, checking, error: e, value: state } = signup;
  const {
    id: checkedUserId,
    email: checkedEmail,
    tel: checkedTel,
    send: checkedSend,
    auth: checkedAuth,
  } = checked;
  const [error, setError] = useState({
    lastName: '',
    firstName: '',
    userId: '',
    password: '',
    email: '',
    tel: '',
  });
  const [headerError, setHeaderError] = useState('');
  const [lastNameFocus, setLastNameFocus] = useState(false);
  const [readOnlyInput, setReadOnlyInput] = useState(false);
  const {
    lastName,
    firstName,
    userId,
    password,
    passwordConfirm,
    institution,
  } = state;

  const lastNameValidation = (value) => {
    setLastNameFocus(true);
    if (!value && !firstName) {
      setError((prev) => ({
        ...prev,
        lastName: '성과 이름을 입력하세요.',
        firstName: '',
      }));
      return;
    }
    if (!value) {
      setError((prev) => ({ ...prev, lastName: '성을 입력하세요.' }));
      return;
    }
    dispatch(setValue({ name: 'lastName', value }));
    if (value && !value.match(/^[a-zA-Z가-힣]+$/)) {
      setError((prev) => ({
        ...prev,
        lastName: '성에 숫자, 공백 및 특수문자는 입력할 수 없습니다.',
      }));
      return;
    }
    if (!firstName) {
      setError((prev) => ({
        ...prev,
        lastName: '',
        firstName: '이름을 입력하세요.',
      }));
      return;
    }
    setError((prev) => ({ ...prev, lastName: '' }));
  };

  const firstNameValidation = (value) => {
    if (!value && !lastName && lastNameFocus) {
      setError((prev) => ({
        ...prev,
        lastName: '성과 이름을 입력하세요.',
        firstName: '',
      }));
      return;
    }
    if (value && !value.match(/^[a-zA-Z가-힣]+$/)) {
      setError((prev) => ({
        ...prev,
        firstName: '이름에 숫자, 공백 및 특수문자는 입력할 수 없습니다.',
      }));
      return;
    }
    if (!value) {
      setError((prev) => ({ ...prev, firstName: '이름을 입력하세요.' }));
      return;
    }
    dispatch(setValue({ name: 'firstName', value }));

    if (!lastName) {
      setError((prev) => ({
        ...prev,
        lastName: '성을 입력하세요.',
        firstName: '',
      }));
      return;
    }
    setError((prev) => ({ ...prev, firstName: '' }));
  };

  const userIdValidation = (value) => {
    const checkChar = ['-', '_', '.'];
    if (value === '') {
      setError((prev) => ({ ...prev, userId: '아이디를 입력하세요.' }));
      return;
    }
    if (!Number.isNaN(Number(value[0]))) {
      setError((prev) => ({
        ...prev,
        userId: '아이디는 숫자로 시작할 수 없습니다.',
      }));
      return;
    }
    if (value.length < 4) {
      setError((prev) => ({
        ...prev,
        userId: '아이디는 최소 4자는 입력해야 합니다.',
      }));
      return;
    }
    if (!value.match(/^[0-9a-zA-Z\-"_""."]+$/)) {
      setError((prev) => ({
        ...prev,
        userId:
          '아이디에는 영문(소문자, 대문자), 숫자, - , _ , . 의 문자만 사용 가능합니다.',
      }));
      return;
    }
    if (checkChar.indexOf(value[value.length - 1]) > -1) {
      setError((prev) => ({
        ...prev,
        userId: '아이디는 - , _ , . 의 문자로 끝날 수 없습니다.',
      }));
      return;
    }

    if (Number.isNaN(value[0])) {
      setError((prev) => ({
        ...prev,
        userId: '아이디는 숫자로 시작할 수 없습니다.',
      }));
      return;
    }

    dispatch(setValue({ name: 'userId', value }));
    setError((prev) => ({
      ...prev,
      userId: checkedUserId
        ? ''
        : '중복 확인을 선택하여 아이디를 중복 체크하세요.',
    }));
  };

  const passwordValidation = (value) => {
    const checkPassword =
      /^(?=.*[A-Za-z])(?=.*[\d~!@#$%^&*_\-+=;:,.?])[A-Za-z\d~!@#$%^&*_\-+=;:,.?]{8,}$/;
    if (value === '') {
      setError((prev) => ({ ...prev, password: '비밀번호를 입력해주세요.' }));
      return;
    }
    if (!checkPassword.test(value)) {
      setError((prev) => ({
        ...prev,
        password:
          '비밀번호는 영문, 숫자 및 특수문자(~!@#$%^&*_-+=;:,.?)를 조합하여 8~20자 이하로 입력해야 합니다.',
      }));
      return;
    }
    dispatch(setValue({ name: 'password', value }));
    if (password !== '' && value !== passwordConfirm) {
      setError((prev) => ({
        ...prev,
        password: '',
        passwordConfirm: '비밀번호가 일치하지 않습니다.',
      }));
      return;
    }
    setError((prev) => ({ ...prev, password: '', passwordConfirm: '' }));
  };

  const passwordConfirmValidation = (value) => {
    dispatch(setValue({ name: 'passwordConfirm', value }));

    if (password !== value && value !== '') {
      setError((prev) => ({
        ...prev,
        passwordConfirm: '비밀번호가 일치하지 않습니다.',
      }));
      return;
    }
    if (
      password === value &&
      error.password !==
        '비밀번호는 영문, 숫자 및 특수문자(~!@#$%^&*_-+=;:,.?)를 조합하여 8~20자 이하로 입력해야 합니다.'
    ) {
      setError((prev) => ({ ...prev, passwordConfirm: '' }));
    }
  };

  const emailValidation = (value) => {
    const isCheckEmail =
      /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i;
    if (!value) {
      setError((prev) => ({ ...prev, email: '이메일을 입력해주세요.' }));
      return;
    }
    if (!isCheckEmail.test(value)) {
      setError((prev) => ({ ...prev, email: '유효한 메일 형식이 아닙니다.' }));
      return;
    }
    if (value.indexOf('@hanmail') > -1 || value.indexOf('@daum') > -1) {
      setError((prev) => ({
        ...prev,
        email:
          'daum 및 hanmail 이메일 계정으로는 인증메일을 수신할 수 없습니다. 다른 메일로 입력해주세요.',
      }));
      return;
    }
    dispatch(setValue({ name: 'email', value }));
    setError((prev) => ({ ...prev, email: '' }));
  };
  const telValidation = (value) => {
    const isCheckTel = /\d{10,}/g;
    if (!value) {
      setError((prev) => ({ ...prev, tel: '이메일을 입력해주세요.' }));
      return;
    }
    if (!isCheckTel.test(value)) {
      setError((prev) => ({
        ...prev,
        tel: '유효한 전화번호 형식이 아닙니다.',
      }));
      return;
    }
    dispatch(setValue({ name: 'tel', value }));
    setError((prev) => ({ ...prev, tel: '' }));
  };

  const validation = (target) => {
    const { name, value } = target;
    switch (name) {
      case 'lastName':
        return lastNameValidation(value);
      case 'firstName':
        return firstNameValidation(value);
      case 'userId':
        return userIdValidation(value);
      case 'password':
        return passwordValidation(value);
      case 'passwordConfirm':
        return passwordConfirmValidation(value);
      case 'email':
        return emailValidation(value);
      case 'tel':
        return telValidation(value);
      default:
        return null;
    }
  };

  const onChange = (event) => {
    if (headerError) setHeaderError('');
    const { name, value } = event.target;
    if (name === 'userId' && value !== state.userId) {
      dispatch(uncheck('id'));
    }
    if (name === 'email' && value !== state.email) {
      dispatch(uncheck('send'));
      dispatch(uncheck('email'));
    }
    if (name === 'email' || name === 'tel') {
      errorCheckList.push(name);
    }
    if (name === 'institution') {
      dispatch(setValue({ name, value }));
    }
    validation({ name, value });
  };

  const onBlur = (event) => {
    if (headerError) setHeaderError('');
    const { name } = event.target;
    const index = errorCheckList.indexOf(name);
    const list = errorCheckList.slice(0, index + 1);
    list.map((check) => validation({ name: check, value: state[check] }));
  };

  const onCheck = (data) => {
    dispatch(checkInfo(data));
  };

  const onConfirm = (data) => {
    dispatch(confirmCode(data));
  };

  const onSend = (data) => {
    setReadOnlyInput(true);
    dispatch(sendVerifyMsg({ ...data, userId }));
  };

  const handleCheckUserId = async () => {
    if (!userId) {
      setError({ ...error, userId: '아이디를 입력하세요.' });
      return;
    }

    dispatch(checkUserId(userId.trim()));
  };

  useUpdateEffect(() => {
    if (!e) return;
    if (e.id) {
      setError({ ...error, userId: e.id });
    }
    if (e.email) {
      setError({ ...error, email: e.email });
    }
    if (e.tel) {
      setError({ ...error, tel: e.tel });
    }
    setError({ ...error, send: e.send, auth: e.auth });
  }, [e]);

  // const handleSendMail = () => {
  //   setReadOnlyInput(true);
  //   dispatch(
  //     signUp({
  //       lastName: lastName.trim(),
  //       firstName: firstName.trim(),
  //       userId: userId.trim(),
  //       password: password.trim(),
  //       email: email.trim(),
  //       tel: tel.trim(),
  //       institution: institution.trim(),
  //       // agreeLocation: location,
  //     }),
  //   );
  // };

  useUpdateEffect(() => {
    if (checkedUserId && error.userId) {
      userIdValidation(userId);
    }
  }, [checkedUserId, checkedEmail, error]);

  return (
    <Grid container spacing={3} maxWidth="sm" sx={{ margin: '24px auto 0' }}>
      <Grid item xs={8}>
        <TextField
          required
          name="userId"
          label="아이디"
          variant="outlined"
          onChange={onChange}
          onBlur={onBlur}
          readOnly={readOnlyInput}
          error={!!error.userId}
          helperText={error.userId}
          InputProps={{
            endAdornment: checkedUserId && <CheckIcon />,
          }}
          defaultValue={state.userId}
          disabled={checkedSend || checkedAuth}
          fullWidth
        />
      </Grid>
      <Grid item xs={4} sx={{ alignSelf: 'center' }}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={handleCheckUserId}
          disabled={checkedUserId}
        >
          중복 확인
        </Button>
      </Grid>
      <Grid item xs={6}>
        <TextField
          required
          name="password"
          label="비밀번호"
          variant="outlined"
          type="password"
          onBlur={onBlur}
          onChange={onChange}
          error={!!error.password}
          helperText={error.password}
          defaultValue={state.password}
          disabled={checkedSend || checkedAuth}
          fullWidth
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          required
          name="passwordConfirm"
          label="비밀번호 확인"
          variant="outlined"
          type="password"
          onBlur={onBlur}
          onChange={onChange}
          error={!!error.passwordConfirm}
          helperText={error.passwordConfirm}
          defaultValue={state.passwordConfirm}
          disabled={checkedSend || checkedAuth}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} md={6}>
        <TextField
          required
          name="lastName"
          label="성"
          variant="outlined"
          onChange={onChange}
          onBlur={onBlur}
          error={!!error.lastName}
          helperText={error.lastName}
          defaultValue={state.lastName}
          disabled={checkedSend || checkedAuth}
          fullWidth
        />
      </Grid>
      <Grid item xs={6} md={6}>
        <TextField
          required
          name="firstName"
          label="이름"
          variant="outlined"
          onChange={onChange}
          onBlur={onBlur}
          error={!!error.firstName}
          helperText={error.firstName}
          defaultValue={state.firstName}
          disabled={checkedSend || checkedAuth}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="institution"
          label="기관명"
          variant="outlined"
          onChange={onChange}
          defaultValue={institution}
          disabled={checkedSend || checkedAuth}
          fullWidth
        />
      </Grid>
      <VerifiedField
        {...{
          checking,
          checkedItem: checkedEmail,
          checkedSend,
          checkedAuth,
          setError,
          state,
          name: 'email',
          label: '이메일',
          error,
          onBlur,
          onChange,
          onCheck,
          onConfirm,
          onSend,
        }}
      />
      <VerifiedField
        {...{
          checking,
          checkedItem: checkedTel,
          checkedSend,
          checkedAuth,
          setError,
          state,
          name: 'tel',
          label: '전화번호',
          error,
          onBlur,
          onChange,
          onCheck,
          onConfirm,
          onSend,
        }}
      />
      {!checkedAuth && (
        <Grid item xs={12}>
          <Alert severity="info">
            이메일 또는 전화번호를 입력하여 인증을 완료하세요.
          </Alert>
        </Grid>
      )}
      {checkedAuth && (
        <Grid item xs={12}>
          <Alert severity="success">
            다음 버튼을 선택하여 가입을 완료하세요.
          </Alert>
        </Grid>
      )}
      {!checking.complete && error.complete && (
        <Grid item xs={12}>
          <Alert severity="error">{error.complete}</Alert>
        </Grid>
      )}
    </Grid>
  );
};
export default SignupContainer;
